import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { QuickAddSelect } from '@/components/shared/quick-add-select';
import { useCreateDepartment, useDepartments } from '@/hooks/use-projects';
import { handleApiError } from '@/lib/api-client';
import type { Department } from '@/lib/types';

interface DepartmentSelectProps {
  value?: number;
  onChange: (departmentId: number | undefined) => void;
  required?: boolean;
}

function DepartmentCreateForm({
  onCreated,
  onCancel,
}: {
  onCreated: (department: Department) => void;
  onCancel: () => void;
}) {
  const createDepartment = useCreateDepartment();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    try {
      const department = await createDepartment.mutateAsync({
        name: name.trim(),
        code: code.trim() || undefined,
      });
      onCreated(department);
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div data-field-container className="space-y-2">
        <Label htmlFor="new-department-name">Department name *</Label>
        <Input
          id="new-department-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="new-department-code">Code</Label>
        <Input
          id="new-department-code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Optional short code"
        />
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={createDepartment.isPending}>
          {createDepartment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create department
        </Button>
      </div>
    </form>
  );
}

export function DepartmentSelect({ value, onChange, required = false }: DepartmentSelectProps) {
  const { data, isLoading } = useDepartments({ page_size: 100, is_active: true });
  const [createdDepartment, setCreatedDepartment] = useState<Department | null>(null);
  const departments = createdDepartment
    ? [
        createdDepartment,
        ...(data?.results ?? []).filter((department) => department.id !== createdDepartment.id),
      ]
    : data?.results ?? [];
  const selected = departments.find((department) => department.id === value) ?? null;

  return (
    <QuickAddSelect<Department>
      value={selected}
      onChange={(department) => onChange(department?.id)}
      options={departments}
      getOptionValue={(department) => department.id}
      getOptionLabel={(department) =>
        department.code ? `${department.code} - ${department.name}` : department.name
      }
      placeholder="Select department"
      isLoading={isLoading}
      required={required}
      requiredMessage="Department is required."
      quickAdd={{
        triggerLabel: 'New department',
        dialogTitle: 'New department',
        dialogDescription: 'Create a department without leaving this form.',
        renderForm: ({ onCreated, onCancel }) => (
          <DepartmentCreateForm
            onCreated={(department) => {
              setCreatedDepartment(department);
              onCreated(department);
            }}
            onCancel={onCancel}
          />
        ),
      }}
    />
  );
}
